import type { RenderSegment } from '../components/ProjectSegment/ProjectSegment.types'

/** `⇧A` darüber, `⇧B` darunter (der Knopf der Pille), `⇧F` ganz oben. */
export type NewSectionWhere = 'above' | 'below' | 'top'

export interface NewSectionPlacementInput {
  /** Die Segmente der Liste, wie `TaskBoard` sie zeichnet. */
  segments: RenderSegment[]
  /** Die markierte Zeile — eine Aufgabe oder ein Abschnittskopf. */
  focused: { type: 'task' | 'section'; id: string } | null
  /** Die Abschnitte im Bild, beim Auslösen gemessen; `null` = das unsortierte Segment. */
  visibleSectionKeys: (string | null)[]
  /** Die Aufgaben, die in den neuen Abschnitt kommen — leer, wenn nicht gepackt wird. */
  packIds: string[]
  where: NewSectionWhere
}

/**
 * Wohin ein neuer Abschnitt kommt, als Anker für `handleAddSection`.
 *
 * Bezogen auf den **aktuellen** Abschnitt: beim Packen der der obersten
 * markierten Aufgabe, sonst der der markierten Zeile, sonst der einzige im
 * Bild. Darüber heißt vor seinen Kopf, darunter vor den nächsten — so bleiben
 * versteckte erledigte Aufgaben in ihrem Abschnitt. Über dem unsortierten Block
 * oben kann kein Abschnitt stehen (er nähme dessen Aufgaben auf): Dort vor den
 * ersten Abschnitt.
 *
 * **„Ganz oben" (⇧F) braucht keinen Anker** (#276): vor alles andere, mit den
 * Aufgaben ohne Abschnitt darin. Am ersten Abschnitt festgemacht, bekam der
 * neue dessen Nummer, wenn der und seine erste Aufgabe dieselbe `order` trugen
 * (Testdaten) — und stand an zweiter Stelle.
 *
 * Ohne aktuellen Abschnitt: darüber → ganz oben, darunter → ans Ende.
 */
export function resolveNewSectionPlacement(
  input: NewSectionPlacementInput,
): { anchorId?: string; placement?: 'before'; position?: 'top' } {
  const { segments, where } = input
  if (where === 'top') return { position: 'top' }

  const firstSectioned = segments.findIndex(segment => segment.section)
  const beforeFirst = firstSectioned === -1 ? {} : { anchorId: segments[firstSectioned].section!.id, placement: 'before' as const }

  const current = currentSegmentIndex(input)
  if (current === -1) return where === 'above' ? beforeFirst : {}

  if (where === 'above') {
    const section = segments[current].section
    return section ? { anchorId: section.id, placement: 'before' } : beforeFirst
  }

  const next = segments.slice(current + 1).find(segment => segment.section)
  return next ? { anchorId: next.section!.id, placement: 'before' } : {}
}

function currentSegmentIndex({ segments, focused, visibleSectionKeys, packIds }: NewSectionPlacementInput): number {
  const ofTask = (taskId: string) => segments.findIndex(segment => segment.tasks.some(task => task.id === taskId))
  if (packIds.length > 0) {
    const indices = packIds.map(ofTask).filter(index => index !== -1)
    if (indices.length > 0) return Math.min(...indices)
  }
  if (focused) {
    const own = focused.type === 'task'
      ? ofTask(focused.id)
      : segments.findIndex(segment => segment.section?.id === focused.id)
    if (own !== -1) return own
  }
  if (visibleSectionKeys.length === 1) {
    return segments.findIndex(segment => (segment.section?.id ?? null) === visibleSectionKeys[0])
  }
  return -1
}
